import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { startOfWeek, endOfWeek, addWeeks, subWeeks, format, addDays } from 'date-fns';
import { eventLocalDate, formatEventDate, isEventDayPast } from '../utils/eventDate';
import { slotsAPI, bookingsAPI, eventCommentsAPI, eventFilesAPI, coursesAPI, gamificationAPI, resolveFileUrl } from '../services/api';
import type { Course } from '../services/api';
import type { EventComment, EventPhoto } from '../services/api';
import type { TrainingSlot } from '../types';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, MouseHandlerDataParam } from 'recharts';
import EditChildModal from '../components/admin/EditChildModal';
import { nearestIndexByDistance, hasFiniteElevation } from '../utils/gpx';

interface NajSportUser {
  id: string;
  email: string;
  name: string | null;
  surname: string | null;
  role: string;
  photoUrl?: string | null;
}

const PURPLE = '#200854';
const ORANGE = '#FF6600';
const GREEN = '#10E56B';
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_I18N = ['days.monShort', 'days.tueShort', 'days.wedShort', 'days.thuShort', 'days.friShort', 'days.satShort', 'days.sunShort'];

// ── Discussion Modal ─────────────────────────────────────────────────────────
const DiscussionModal: React.FC<{
  slot: TrainingSlot;
  currentUser: NajSportUser | null;
  onClose: () => void;
}> = ({ slot, currentUser, onClose }) => {
  const { t } = useTranslation();
  const [comments, setComments] = useState<EventComment[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [sendError, setSendError] = useState('');
  const [editError, setEditError] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const isAdmin = currentUser?.role === 'ADMIN' || currentUser?.role === 'POWER_ADMIN';

  const fetchComments = useCallback(async () => {
    try {
      const data = await eventCommentsAPI.getComments(slot.id);
      setComments(data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [slot.id]);

  useEffect(() => {
    fetchComments();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchComments();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchComments]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments.length]);

  const handleSend = async () => {
    if (!text.trim() || sending) return;
    if (text.trim().length > 280) {
      setSendError(t('najsport.commentTooLong'));
      return;
    }
    setSending(true);
    setSendError('');
    try {
      const comment = await eventCommentsAPI.createComment(slot.id, text.trim());
      setComments((prev) => [...prev, comment]);
      setText('');
    } catch (err: any) {
      const apiMsg = err?.response?.data?.error;
      setSendError(apiMsg || t('najsport.commentSendFailed'));
    }
    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const startEdit = (c: EventComment) => {
    setEditingId(c.id);
    setEditText(c.text);
    setEditError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditText('');
    setEditError('');
  };

  const saveEdit = async (commentId: string) => {
    const trimmed = editText.trim();
    if (!trimmed || busyId) return;
    if (trimmed.length > 280) {
      setEditError(t('najsport.commentTooLong'));
      return;
    }
    setBusyId(commentId);
    setEditError('');
    try {
      const updated = await eventCommentsAPI.updateComment(slot.id, commentId, trimmed);
      setComments((prev) => prev.map((c) => (c.id === commentId ? updated : c)));
      setEditingId(null);
      setEditText('');
    } catch (err: any) {
      const apiMsg = err?.response?.data?.error;
      setEditError(apiMsg || t('najsport.commentSendFailed'));
    }
    setBusyId(null);
  };

  const handleDelete = async (commentId: string) => {
    if (busyId) return;
    if (!window.confirm(t('najsport.confirmDeleteComment'))) return;
    setBusyId(commentId);
    try {
      await eventCommentsAPI.deleteComment(slot.id, commentId);
      setComments((prev) => prev.filter((c) => c.id !== commentId));
    } catch { /* ignore */ }
    setBusyId(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '80vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ backgroundColor: PURPLE }}>
          <div>
            <h3 className="text-sm font-bold text-white">{slot.title}</h3>
            <p className="text-[11px] text-purple-200">{formatEventDate(slot.date, 'd MMM')} · {slot.startTime}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-xl leading-none p-1" aria-label={t('najsport.close')}>✕</button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2 min-h-[200px]">
          {loading ? (
            <div className="text-center text-gray-400 text-sm py-8">{t('najsport.loading')}</div>
          ) : comments.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-8">{t('najsport.noComments')}</div>
          ) : (
            comments.map((c) => {
              const isOwn = currentUser?.id === c.userId;
              const canEdit = isOwn;
              const canDelete = isOwn || isAdmin;
              const isEditing = editingId === c.id;
              return (
                <div key={c.id} className="flex gap-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0 mt-0.5" style={{ backgroundColor: PURPLE }}>
                    {(c.user.name?.[0] || c.user.email?.[0] || '?').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-semibold" style={{ color: PURPLE }}>
                        {c.user.name && c.user.surname ? `${c.user.name} ${c.user.surname}` : c.user.email}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        {format(new Date(c.createdAt), 'HH:mm')}
                      </span>
                    </div>
                    {isEditing ? (
                      <div className="mt-1">
                        <div className="flex flex-wrap gap-1 items-center">
                          <input
                            type="text"
                            value={editText}
                            onChange={(e) => { setEditText(e.target.value.slice(0, 280)); if (editError) setEditError(''); }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); saveEdit(c.id); }
                              if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                            }}
                            maxLength={280}
                            autoFocus
                            className="flex-1 min-w-0 px-2 py-1 border border-gray-200 rounded text-base sm:text-sm focus:outline-none focus:border-purple-400"
                          />
                          <button
                            type="button"
                            onClick={() => saveEdit(c.id)}
                            disabled={!editText.trim() || busyId === c.id}
                            className="px-2 py-1 rounded text-white text-xs font-bold disabled:opacity-40"
                            style={{ backgroundColor: ORANGE }}
                          >
                            {t('najsport.save')}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="px-2 py-1 rounded text-xs text-gray-500 hover:text-gray-700"
                          >
                            {t('najsport.cancel')}
                          </button>
                        </div>
                        <div className="flex justify-between items-center mt-0.5">
                          {editError ? (
                            <span className="text-[11px] text-red-600">{editError}</span>
                          ) : <span />}
                          <span className="text-[10px] text-gray-400">{editText.length}/280</span>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-700 break-words">{c.text}</p>
                        {(canEdit || canDelete) && (
                          <div className="mt-0.5 flex gap-3 text-[11px] text-gray-400">
                            {canEdit && (
                              <button
                                type="button"
                                onClick={() => startEdit(c)}
                                className="hover:text-purple-700"
                              >
                                {t('najsport.edit')}
                              </button>
                            )}
                            {canDelete && (
                              <button
                                type="button"
                                onClick={() => handleDelete(c.id)}
                                disabled={busyId === c.id}
                                className="hover:text-red-600 disabled:opacity-40"
                              >
                                {t('najsport.delete')}
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        <div className="border-t px-3 py-2 flex gap-2 items-center">
          <input
            type="text"
            value={text}
            onChange={(e) => { setText(e.target.value.slice(0, 280)); if (sendError) setSendError(''); }}
            onKeyDown={handleKeyDown}
            placeholder={t('najsport.writeComment')}
            className="flex-1 min-w-0 px-3 py-2 border border-gray-200 rounded-lg text-base sm:text-sm focus:outline-none focus:border-purple-400"
            maxLength={280}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={!text.trim() || sending}
            className="px-3 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-40 transition-colors"
            style={{ backgroundColor: ORANGE }}
          >
            ➤
          </button>
        </div>
        <div className="flex justify-between items-center px-3 pb-1">
          {sendError ? (
            <span className="text-[11px] text-red-600">{sendError}</span>
          ) : <span />}
          <span className="text-[10px] text-gray-400">{text.length}/280</span>
        </div>
      </div>
    </div>
  );
};

// ── Equipment Reminder Popup ─────────────────────────────────────────────────
const EquipmentReminderModal: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [photoConsent, setPhotoConsent] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onCancel}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className="relative bg-white w-full sm:w-[90%] max-w-sm rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden animate-[slideUp_0.3s_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className="relative px-5 pt-5 pb-4 text-white overflow-hidden" style={{ background: `linear-gradient(135deg, ${PURPLE} 0%, #4a1d96 100%)` }}>
          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full opacity-10 bg-white" />
          <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full opacity-10 bg-white" />
          <div className="relative">
            <h3 className="text-lg font-bold flex items-center gap-2">
              🚴 {t('najsport.bikeEquip')}
            </h3>
            <p className="text-xs text-purple-200 mt-1">{t('najsport.checkBefore')}</p>
          </div>
        </div>

        {/* Checklist items */}
        <div className="px-5 py-4 space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-red-50 border border-red-200">
            <span className="text-2xl">🪖</span>
            <p className="text-sm font-bold text-red-600">{t('najsport.helmet')}</p>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
            <span className="text-2xl">🔧</span>
            <div>
              <p className="text-sm font-semibold" style={{ color: PURPLE }}>{t('najsport.haveBasicGear')}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{t('najsport.basicGearList')}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-gray-50">
              <span className="text-2xl">💧</span>
              <p className="text-xs font-semibold" style={{ color: PURPLE }}>{t('najsport.water')}</p>
            </div>
            <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-gray-50">
              <span className="text-2xl">🍌</span>
              <p className="text-xs font-semibold" style={{ color: PURPLE }}>{t('najsport.food')}</p>
            </div>
            <div className="flex flex-col items-center gap-1 p-3 rounded-xl bg-gray-50">
              <span className="text-2xl">🔦</span>
              <p className="text-xs font-semibold" style={{ color: PURPLE }}>{t('najsport.lights')}</p>
            </div>
          </div>

          {/* Photo consent */}
          <div className="border-t pt-3">
            <button
              type="button"
              onClick={() => setPhotoConsent(!photoConsent)}
              className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all duration-200 ${
                photoConsent ? 'border-purple-400 bg-purple-50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <span className="text-xl">📸</span>
              <span className="flex-1 text-left text-xs text-gray-600">{t('najsport.photoConsent')}</span>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-200 ${
                photoConsent ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
              }`}>
                {photoConsent && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="px-5 pb-5 flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-3 rounded-xl text-sm font-medium border-2 border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
          >
            {t('najsport.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!photoConsent}
            className="flex-1 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-30 transition-all duration-200 hover:shadow-lg"
            style={{ backgroundColor: ORANGE }}
          >
            {t('najsport.confirmAndSignUp')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── GPX Map Modal ────────────────────────────────────────────────────────────

const GpxMapModal: React.FC<{
  slot: TrainingSlot;
  onClose: () => void;
  onChangeCourse?: () => void;
}> = ({ slot, onClose, onChangeCourse }) => {
  const { t } = useTranslation();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const hoverMarkerRef = useRef<L.CircleMarker | null>(null);
  const pointsRef = useRef<[number, number][]>([]);
  const cumDistRef = useRef<number[]>([]);
  const profileRef = useRef<{ d: number; ele: number }[]>([]);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<{ distanceKm: number; elevationGain: number; hasElevation: boolean } | null>(null);
  const [profile, setProfile] = useState<{ d: number; ele: number }[]>([]);

  useEffect(() => {
    if (!mapRef.current || !slot.gpxFileUrl) return;

    let isCancelled = false;
    const abortController = new AbortController();

    // Delay initialization so the DOM element has dimensions
    const timer = setTimeout(() => {
      if (!mapRef.current || isCancelled) return;

    const map = L.map(mapRef.current).setView([48.7, 19.7], 8);
    mapInstanceRef.current = map;

    // Force Leaflet to recalculate container size
    setTimeout(() => map.invalidateSize(), 0);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);

    fetch(resolveFileUrl(slot.gpxFileUrl), {
      headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      signal: abortController.signal,
    })
      .then((r) => {
        // A 401/404 body would otherwise parse as XML and surface as the
        // misleading "no points" error instead of a load failure.
        if (!r.ok) throw new Error(`GPX fetch failed: ${r.status}`);
        return r.text();
      })
      .then((gpxText) => {
        // The modal may have been closed (map torn down) while the fetch was
        // in flight — bail out before touching the removed Leaflet instance.
        if (isCancelled) return;
        const parser = new DOMParser();
        const gpxDoc = parser.parseFromString(gpxText, 'text/xml');
        const points: L.LatLngExpression[] = [];
        const elevations: number[] = [];

        const readPoint = (pt: Element) => {
          const lat = parseFloat(pt.getAttribute('lat') || '0');
          const lon = parseFloat(pt.getAttribute('lon') || '0');
          if (!lat || !lon) return;
          points.push([lat, lon]);
          const eleEl = pt.querySelector('ele');
          const ele = eleEl ? parseFloat(eleEl.textContent || '') : NaN;
          elevations.push(Number.isFinite(ele) ? ele : NaN);
        };

        // Parse <trkpt> elements
        gpxDoc.querySelectorAll('trkpt').forEach(readPoint);

        // Fallback: parse <rtept> elements
        if (points.length === 0) {
          gpxDoc.querySelectorAll('rtept').forEach(readPoint);
        }

        if (points.length > 0) {
          const polyline = L.polyline(points, { color: PURPLE, weight: 4, opacity: 0.8 }).addTo(map);
          map.fitBounds(polyline.getBounds(), { padding: [30, 30] });

          // Start/end markers
          L.circleMarker(points[0] as L.LatLngExpression, { radius: 8, color: '#10B981', fillColor: '#10B981', fillOpacity: 1 }).addTo(map).bindPopup(t('najsport.start'));
          L.circleMarker(points[points.length - 1] as L.LatLngExpression, { radius: 8, color: '#EF4444', fillColor: '#EF4444', fillOpacity: 1 }).addTo(map).bindPopup(t('najsport.finish'));

          // Distance: Haversine summed across consecutive points, with per-point cumulative distance
          const toRad = (d: number) => (d * Math.PI) / 180;
          const cumDistKm: number[] = new Array(points.length);
          cumDistKm[0] = 0;
          let distanceM = 0;
          for (let i = 1; i < points.length; i++) {
            const [lat1, lon1] = points[i - 1] as [number, number];
            const [lat2, lon2] = points[i] as [number, number];
            const dLat = toRad(lat2 - lat1);
            const dLon = toRad(lon2 - lon1);
            const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
            distanceM += 6371000 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
            cumDistKm[i] = distanceM / 1000;
          }

          // Elevation gain: sum positive deltas with a small threshold to filter GPS noise
          let elevationGain = 0;
          let lastEle: number | null = null;
          const THRESHOLD = 3; // meters
          for (const e of elevations) {
            if (!Number.isFinite(e)) continue;
            if (lastEle === null) { lastEle = e; continue; }
            const diff = e - lastEle;
            if (diff > THRESHOLD) { elevationGain += diff; lastEle = e; }
            else if (diff < -THRESHOLD) { lastEle = e; }
          }

          // A GPX track may carry no <ele> readings at all (e.g. a plain
          // route export) — don't report a confident "0 m" gain for those.
          const hasElevation = hasFiniteElevation(elevations);
          setStats({ distanceKm: distanceM / 1000, elevationGain: Math.round(elevationGain), hasElevation });

          // Build elevation profile (downsample to keep chart light on long tracks)
          if (hasElevation) {
            const MAX_POINTS = 200;
            const step = Math.max(1, Math.floor(points.length / MAX_POINTS));
            const prof: { d: number; ele: number }[] = [];
            for (let i = 0; i < points.length; i += step) {
              if (Number.isFinite(elevations[i])) {
                prof.push({ d: cumDistKm[i], ele: Math.round(elevations[i]) });
              }
            }
            // Stepping can skip the final track point — always close the
            // profile at the full distance so the x-domain reaches the end.
            const lastIdx = points.length - 1;
            if (Number.isFinite(elevations[lastIdx]) && prof.length > 0 && prof[prof.length - 1].d < cumDistKm[lastIdx]) {
              prof.push({ d: cumDistKm[lastIdx], ele: Math.round(elevations[lastIdx]) });
            }
            profileRef.current = prof;
            setProfile(prof);
            // The profile panel shrinks the map container (450→340px) on the
            // next render — refit so the route's southern edge isn't clipped.
            setTimeout(() => {
              if (!isCancelled && mapInstanceRef.current) {
                mapInstanceRef.current.invalidateSize();
                mapInstanceRef.current.fitBounds(polyline.getBounds(), { padding: [30, 30] });
              }
            }, 0);
          }
          pointsRef.current = points as [number, number][];
          cumDistRef.current = cumDistKm;
        } else {
          setError(t('najsport.gpxNoPoints'));
        }
      })
      .catch((err) => {
        if (isCancelled || err?.name === 'AbortError') return;
        setError(t('najsport.gpxLoadError'));
      });
    }, 50);

    return () => {
      isCancelled = true;
      abortController.abort();
      clearTimeout(timer);
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
      // Reset per-track state so a gpxFileUrl swap without unmount can't
      // leave a hover marker bound to the removed map or show stale stats.
      hoverMarkerRef.current = null;
      pointsRef.current = [];
      cumDistRef.current = [];
      profileRef.current = [];
      setStats(null);
      setProfile([]);
      setError('');
    };
  }, [slot.gpxFileUrl]);

  const handleProfileHover = (state: MouseHandlerDataParam) => {
    const map = mapInstanceRef.current;
    if (!map || pointsRef.current.length === 0) return;
    // recharts v3 handlers receive MouseHandlerDataParam (no activePayload).
    // activeIndex (index into the chart data) updates on every mousemove;
    // activeLabel proved unreliable mid-drag in live testing, so it's only
    // the fallback.
    const activeIdx = Number(state.activeIndex);
    let d: number = NaN;
    if (Number.isFinite(activeIdx) && profileRef.current[activeIdx]) {
      d = profileRef.current[activeIdx].d;
    } else {
      d = typeof state.activeLabel === 'number' ? state.activeLabel : Number(state.activeLabel);
    }
    if (!Number.isFinite(d)) return;
    // Find the nearest GPX point to this distance by cumulative distance
    // (not by index ratio — point spacing is not uniform: stops cluster many
    // points at the same distance, fast descents spread few points over a lot
    // of distance).
    const idx = nearestIndexByDistance(cumDistRef.current, d);
    if (idx < 0) return;
    const latlng = pointsRef.current[idx];
    if (!latlng) return;
    if (!hoverMarkerRef.current) {
      hoverMarkerRef.current = L.circleMarker(latlng, {
        radius: 7, color: '#fff', weight: 2, fillColor: ORANGE, fillOpacity: 1,
      }).addTo(map);
    } else {
      hoverMarkerRef.current.setLatLng(latlng);
    }
  };

  const handleProfileLeave = () => {
    if (hoverMarkerRef.current && mapInstanceRef.current) {
      mapInstanceRef.current.removeLayer(hoverMarkerRef.current);
      hoverMarkerRef.current = null;
    }
  };

  const handleDownload = async () => {
    if (!slot.gpxFileUrl) return;
    try {
      const resp = await fetch(resolveFileUrl(slot.gpxFileUrl), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${slot.title.replace(/\s+/g, '_')}.gpx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white w-[95%] max-w-2xl rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ backgroundColor: PURPLE }}>
          <div>
            <h3 className="text-sm font-bold text-white">{slot.courseName || slot.title} — {t('najsport.route')}</h3>
            <p className="text-[11px] text-purple-200">
              {formatEventDate(slot.date, 'd MMM')} · {slot.startTime}
              {stats && (
                <>
                  {' · '}
                  <span title={t('najsport.distance')}>📏 {stats.distanceKm.toFixed(1)} km</span>
                  {stats.hasElevation && (
                    <>
                      {' · '}
                      <span title={t('najsport.elevationGain')}>⛰ {stats.elevationGain} m</span>
                    </>
                  )}
                </>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handleDownload} className="text-xs px-3 py-1 rounded-lg text-white font-medium" style={{ backgroundColor: ORANGE }}>
              {t('najsport.downloadGpx')}
            </button>
            {onChangeCourse && (
              <button onClick={onChangeCourse} className="text-xs px-3 py-1 rounded-lg font-medium bg-white/20 text-white hover:bg-white/30">
                {t('najsport.changeRoute')}
              </button>
            )}
            <button onClick={onClose} className="text-white/70 hover:text-white text-xl leading-none p-1" aria-label={t('najsport.close')}>✕</button>
          </div>
        </div>
        {error ? (
          <div className="p-8 text-center text-red-500 text-sm">{error}</div>
        ) : (
          <>
            <div ref={mapRef} style={{ height: profile.length > 0 ? '340px' : '450px', width: '100%' }} />
            {profile.length > 0 && (
              <div className="border-t" style={{ height: '130px', padding: '6px 8px 4px' }}>
                <div className="text-[10px] text-gray-500 px-2 pb-1">{t('najsport.elevationProfile')}</div>
                <ResponsiveContainer width="100%" height="85%">
                  <AreaChart
                    data={profile}
                    margin={{ top: 2, right: 8, left: 0, bottom: 0 }}
                    onMouseMove={handleProfileHover}
                    onMouseLeave={handleProfileLeave}
                  >
                    <defs>
                      <linearGradient id="eleFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={PURPLE} stopOpacity={0.6} />
                        <stop offset="100%" stopColor={PURPLE} stopOpacity={0.05} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="d"
                      type="number"
                      domain={[0, 'dataMax']}
                      tickFormatter={(v: number) => `${v.toFixed(0)}`}
                      tick={{ fontSize: 10 }}
                      unit=" km"
                    />
                    <YAxis
                      dataKey="ele"
                      domain={['dataMin', 'dataMax']}
                      tick={{ fontSize: 10 }}
                      width={36}
                      unit=" m"
                    />
                    <Tooltip
                      formatter={((value: unknown, name: unknown) =>
                        name === 'ele' ? [`${value} m`, t('najsport.elevation')] : [value as string | number, name as string]) as never}
                      labelFormatter={((label: unknown) => `${(label as number).toFixed(2)} km`) as never}
                      contentStyle={{ fontSize: 11, padding: '4px 8px' }}
                    />
                    <Area type="monotone" dataKey="ele" stroke={PURPLE} fill="url(#eleFill)" strokeWidth={2} isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ── Photo Gallery Modal ──────────────────────────────────────────────────────
const PhotoGalleryModal: React.FC<{
  slot: TrainingSlot;
  userId: string | null;
  isAdmin: boolean;
  onClose: () => void;
  onPhotosChanged: () => void;
}> = ({ slot, userId, isAdmin, onClose, onPhotosChanged }) => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState<EventPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);
  const [photoError, setPhotoError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchPhotos = useCallback(async () => {
    try {
      const data = await eventFilesAPI.getPhotos(slot.id);
      setPhotos(data);
    } catch (err) { console.error('Fetch photos error:', err); }
    setLoading(false);
  }, [slot.id]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    setPhotoError('');
    try {
      await eventFilesAPI.uploadPhotos(slot.id, Array.from(files));
      await fetchPhotos();
      onPhotosChanged();
    } catch {
      setPhotoError(t('najsport.photoUploadError'));
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (photoId: string) => {
    setPhotoError('');
    try {
      await eventFilesAPI.deletePhoto(photoId);
      setPhotos((prev) => prev.filter((p) => p.id !== photoId));
      setSelectedPhoto(null);
      onPhotosChanged();
    } catch {
      setPhotoError(t('najsport.photoDeleteError'));
    }
  };

  // Fullscreen photo view
  if (selectedPhoto) {
    const photo = photos.find((p) => p.id === selectedPhoto);
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90" onClick={() => setSelectedPhoto(null)}>
        <button className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl" onClick={() => setSelectedPhoto(null)}>✕</button>
        {photo && (isAdmin || photo.uploadedById === userId) && (
          <button
            className="absolute top-4 left-4 text-xs px-3 py-1.5 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700"
            onClick={(e) => { e.stopPropagation(); handleDelete(selectedPhoto); }}
          >
            {t('najsport.delete')}
          </button>
        )}
        {photo && (
          <img src={resolveFileUrl(photo.url)} alt="" className="max-h-[90vh] max-w-[95vw] object-contain" onClick={(e) => e.stopPropagation()} />
        )}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl flex flex-col"
        style={{ maxHeight: '80vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ backgroundColor: PURPLE }}>
          <h3 className="text-sm font-bold text-white">{slot.title} — {t('najsport.photos')}</h3>
          <div className="flex items-center gap-2">
            <input ref={fileInputRef} type="file" accept="image/*" multiple onChange={handleUpload} className="hidden" />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="text-xs px-3 py-1 rounded-lg text-white font-medium disabled:opacity-50"
              style={{ backgroundColor: ORANGE }}
            >
              {uploading ? t('najsport.uploading') : t('najsport.upload')}
            </button>
            <button onClick={onClose} className="text-white/70 hover:text-white text-xl leading-none p-1" aria-label={t('najsport.close')}>✕</button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {photoError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-xs mb-2">
              {photoError}
            </div>
          )}
          {loading ? (
            <div className="text-center text-gray-400 text-sm py-8">{t('najsport.loading')}</div>
          ) : photos.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-8">{t('najsport.noPhotos')}</div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setSelectedPhoto(photo.id)}
                >
                  <img src={resolveFileUrl(photo.url)} alt="" className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Attendance Modal (admin only) ─────────────────────────────────────────────
const AttendanceModal: React.FC<{
  slot: TrainingSlot;
  onClose: () => void;
  onConfirmed: () => void;
}> = ({ slot, onClose, onConfirmed }) => {
  const { t } = useTranslation();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    slot.attendees.forEach(a => { if (a.attended) initial.add(a.id); });
    return initial;
  });
  const [saving, setSaving] = useState(false);

  const toggle = (userId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(slot.attendees.map(a => a.id)));
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await slotsAPI.confirmAttendance(slot.id, Array.from(selectedIds));
      onConfirmed();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const formatName = (a: typeof slot.attendees[0]) =>
    a.name && a.surname ? `${a.name} ${a.surname}` : a.email || '?';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white w-[90%] max-w-sm rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b" style={{ backgroundColor: PURPLE }}>
          <h3 className="text-sm font-bold text-white">{t('najsport.attendance')} — {slot.title}</h3>
          <p className="text-[11px] text-purple-200">{formatEventDate(slot.date, 'd MMM')} · {slot.startTime}</p>
        </div>
        <div className="p-4 space-y-2 max-h-[60vh] overflow-y-auto">
          {slot.attendees.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-4">{t('najsport.noAttendees')}</div>
          ) : (
            <>
              <button
                type="button"
                onClick={selectAll}
                className="text-xs text-purple-600 hover:underline mb-1"
              >
                {t('najsport.selectAll')}
              </button>
              {slot.attendees.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => toggle(a.id)}
                  className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                    selectedIds.has(a.id)
                      ? 'text-white shadow-sm'
                      : 'text-gray-600 border-2 border-gray-200 hover:border-gray-300'
                  }`}
                  style={selectedIds.has(a.id) ? { backgroundColor: PURPLE } : {}}
                >
                  {formatName(a)}
                </button>
              ))}
            </>
          )}
        </div>
        {slot.attendees.length > 0 && (
          <div className="px-4 py-3 border-t flex gap-2">
            <button
              onClick={handleConfirm}
              disabled={saving}
              className="flex-1 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: ORANGE }}
            >
              {saving ? t('najsport.saving') : `${t('najsport.confirmCount')} (${selectedIds.size}/${slot.attendees.length})`}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100"
            >
              {t('najsport.cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Course Selector Modal (admin only) ───────────────────────────────────────
const CourseSelectorModal: React.FC<{
  slot: TrainingSlot;
  onClose: () => void;
  onChanged: () => void;
  onViewMap: (slot: TrainingSlot) => void;
}> = ({ slot, onClose, onChanged, onViewMap }) => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState<Course[]>([]);
  const [selected, setSelected] = useState<string>(slot.courseId || '');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    coursesAPI.getCourses().then((data) => {
      setCourses(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await slotsAPI.updateSlot(slot.id, { courseId: selected || null });
      onChanged();
      onClose();
    } catch { /* ignore */ }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white w-[90%] max-w-sm rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b" style={{ backgroundColor: PURPLE }}>
          <h3 className="text-sm font-bold text-white">{t('najsport.selectRoute')} — {slot.title}</h3>
        </div>
        <div className="p-4 space-y-3">
          {loading ? (
            <div className="text-center text-gray-400 text-sm py-4">{t('najsport.loading')}</div>
          ) : (
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="w-full px-3 py-2 border-2 border-gray-200 rounded-lg text-sm focus:outline-none focus:border-purple-500"
            >
              <option value="">{t('najsport.noRoute')}</option>
              {courses.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="flex-1 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: ORANGE }}
            >
              {saving ? t('najsport.saving') : t('najsport.save')}
            </button>
            {slot.gpxFileUrl && (
              <button
                onClick={() => { onClose(); onViewMap(slot); }}
                className="px-4 py-2 rounded-lg text-sm font-medium border-2 border-gray-200 hover:bg-gray-50"
                style={{ color: PURPLE }}
              >
                {t('najsport.map')}
              </button>
            )}
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100"
            >
              {t('najsport.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Competition Results Modal (admin only) ───────────────────────────────────
const CompetitionResultsModal: React.FC<{
  slot: TrainingSlot;
  onClose: () => void;
  onSaved: () => void;
}> = ({ slot, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [results, setResults] = useState<{ userId: string; place: number }[]>([
    { userId: '', place: 1 },
    { userId: '', place: 2 },
    { userId: '', place: 3 },
  ]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const formatName = (a: typeof slot.attendees[0]) =>
    a.name && a.surname ? `${a.name} ${a.surname}` : a.email || '?';

  const handleSave = async () => {
    const valid = results.filter(r => r.userId);
    if (valid.length === 0) return;
    // Guard against same rider picked twice
    const ids = valid.map(r => r.userId);
    if (new Set(ids).size !== ids.length) {
      setError(t('najsport.duplicateRider'));
      return;
    }
    setSaving(true);
    setError('');
    try {
      await gamificationAPI.setCompetitionResults(slot.id, valid);
      onSaved();
      onClose();
    } catch {
      setError(t('najsport.failedToSaveResults'));
    }
    setSaving(false);
  };

  const hasAny = results.some(r => r.userId);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative bg-white w-[90%] max-w-sm rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b" style={{ backgroundColor: PURPLE }}>
          <h3 className="text-sm font-bold text-white">{t('najsport.competitionResults')} — {slot.title}</h3>
          <p className="text-[11px] text-purple-200">{formatEventDate(slot.date, 'd MMM')} · {slot.startTime}</p>
        </div>
        <div className="p-4 space-y-3">
          {slot.attendees.length === 0 ? (
            <div className="text-center text-gray-400 text-sm py-4">{t('najsport.noAttendees')}</div>
          ) : (
            <>
              {[1, 2, 3].map((place) => (
                <div key={place} className="flex items-center gap-3">
                  <span className="w-8 text-center text-2xl">
                    {place === 1 ? '🥇' : place === 2 ? '🥈' : '🥉'}
                  </span>
                  <select
                    value={results[place - 1]?.userId || ''}
                    onChange={(e) => {
                      const next = [...results];
                      next[place - 1] = { userId: e.target.value, place };
                      setResults(next);
                    }}
                    className="flex-1 border rounded px-2 py-2 text-base sm:text-sm"
                  >
                    <option value="">{t('najsport.selectRider')}</option>
                    {slot.attendees.map((a) => (
                      <option key={a.id} value={a.id}>{formatName(a)}</option>
                    ))}
                  </select>
                  <span className="text-xs text-gray-500 w-10 text-right">
                    +{place === 1 ? 30 : place === 2 ? 20 : 10}
                  </span>
                </div>
              ))}
              {error && <div className="text-xs text-red-600">{error}</div>}
            </>
          )}
        </div>
        {slot.attendees.length > 0 && (
          <div className="px-4 py-3 border-t flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !hasAny}
              className="flex-1 py-2 rounded-lg text-white text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: ORANGE }}
            >
              {saving ? t('najsport.saving') : t('najsport.saveResults')}
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm font-medium text-gray-500 hover:bg-gray-100"
            >
              {t('najsport.cancel')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Main Events Page ─────────────────────────────────────────────────────────
const NajSportEvents: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [user] = useState<NajSportUser | null>(() => {
    const stored = localStorage.getItem('najsportUser');
    return stored ? JSON.parse(stored) : null;
  });

  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [slots, setSlots] = useState<TrainingSlot[]>([]);
  const [myBookings, setMyBookings] = useState<string[]>([]);
  const [bookingMap, setBookingMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [selectedDayIndex, setSelectedDayIndex] = useState(() => {
    const dow = new Date().getDay();
    return dow === 0 ? 6 : dow - 1;
  });

  // Modal states
  const [discussionSlot, setDiscussionSlot] = useState<TrainingSlot | null>(null);
  const [bookingSlotId, setBookingSlotId] = useState<string | null>(null);
  const [gpxSlot, setGpxSlot] = useState<TrainingSlot | null>(null);
  const [courseSelectSlot, setCourseSelectSlot] = useState<TrainingSlot | null>(null);
  const [attendanceSlot, setAttendanceSlot] = useState<TrainingSlot | null>(null);
  const [expandedAttendees, setExpandedAttendees] = useState<string | null>(null);
  const [photoSlot, setPhotoSlot] = useState<TrainingSlot | null>(null);
  const [kebabSlotId, setKebabSlotId] = useState<string | null>(null);
  const [editSlot, setEditSlot] = useState<TrainingSlot | null>(null);
  const [competitionSlot, setCompetitionSlot] = useState<TrainingSlot | null>(null);
  const [infoPinnedId, setInfoPinnedId] = useState<string | null>(null);
  const [infoHoverId, setInfoHoverId] = useState<string | null>(null);
  const kebabRef = useRef<HTMLDivElement>(null);

  const isTrainerOrAdmin = user?.role === 'TRAINER' || user?.role === 'ADMIN' || user?.role === 'POWER_ADMIN';
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'POWER_ADMIN';
  const fetchAbortRef = useRef<AbortController | null>(null);

  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!user) navigate('/');
    // Scroll to top after mount — delayed for mobile keyboard dismiss
    const timer = setTimeout(() => {
      topRef.current?.scrollIntoView();
      window.scrollTo(0, 0);
    }, 150);
    return () => clearTimeout(timer);
  }, [user, navigate]);

  // Close kebab menu on outside click/touch
  useEffect(() => {
    if (!kebabSlotId) return;
    const handler = () => setKebabSlotId(null);
    // Delay adding listener so it doesn't fire on the same tap that opened the menu
    const timer = setTimeout(() => {
      document.addEventListener('click', handler);
    }, 10);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handler);
    };
  }, [kebabSlotId]);

  // Close pinned info popover on outside click/touch
  useEffect(() => {
    if (!infoPinnedId) return;
    const handler = () => setInfoPinnedId(null);
    const timer = setTimeout(() => {
      document.addEventListener('click', handler);
    }, 10);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handler);
    };
  }, [infoPinnedId]);

  const weekStart = useMemo(() => startOfWeek(currentWeek, { weekStartsOn: 1 }), [currentWeek]);
  const weekEnd = useMemo(() => endOfWeek(currentWeek, { weekStartsOn: 1 }), [currentWeek]);
  const weekKey = format(weekStart, 'yyyy-MM-dd');

  const dayDates = useMemo(() =>
    Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );

  const fetchWeek = useCallback(async () => {
    // Cancel any in-flight request from a previous week navigation
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    try {
      setLoading(true);
      setError('');
      const [slotsData, bookingsData] = await Promise.all([
        slotsAPI.getSlots(weekKey),
        bookingsAPI.getMyBookings().catch(() => []),
      ]);

      if (controller.signal.aborted) return;

      setSlots(slotsData);

      const map: Record<string, string> = {};
      const bookedSlotIds: string[] = [];
      (bookingsData as any[]).forEach((b) => {
        map[b.trainingSlotId] = b.id;
        bookedSlotIds.push(b.trainingSlotId);
      });
      setBookingMap(map);
      setMyBookings(bookedSlotIds);
    } catch {
      if (controller.signal.aborted) return;
      setError(t('najsport.errorLoadRides'));
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [weekKey]);

  useEffect(() => {
    if (user) fetchWeek();
    return () => {
      if (fetchAbortRef.current) fetchAbortRef.current.abort();
    };
  }, [user, fetchWeek]);

  const slotsByDay = useMemo(() => {
    const grouped: Record<string, TrainingSlot[]> = {};
    DAY_KEYS.forEach((d) => (grouped[d] = []));
    slots.forEach((slot) => {
      // Weekday of the event's Bratislava calendar day — browser-TZ-safe
      const dow = eventLocalDate(slot.date).getDay();
      const idx = dow === 0 ? 6 : dow - 1;
      grouped[DAY_KEYS[idx]]?.push(slot);
    });
    Object.values(grouped).forEach((arr) =>
      arr.sort((a, b) => a.startTime.localeCompare(b.startTime)),
    );
    return grouped;
  }, [slots]);

  const handleBookConfirmed = async (slotId: string) => {
    setBookingSlotId(null);
    setActionLoading(slotId);
    setError('');
    try {
      await bookingsAPI.createBooking(slotId);
      await fetchWeek();
    } catch (err: any) {
      setError(err.response?.data?.error || t('najsport.errorSignUp'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleBook = (slotId: string) => {
    setBookingSlotId(slotId);
  };

  const [cancelConfirmSlotId, setCancelConfirmSlotId] = useState<string | null>(null);

  const handleCancel = (slotId: string) => {
    setCancelConfirmSlotId(slotId);
  };

  const handleCancelConfirmed = async () => {
    const slotId = cancelConfirmSlotId;
    if (!slotId) return;
    setCancelConfirmSlotId(null);
    const bookingId = bookingMap[slotId];
    if (!bookingId) return;
    setActionLoading(slotId);
    setError('');
    try {
      await bookingsAPI.deleteBooking(bookingId);
      await fetchWeek();
    } catch (err: any) {
      setError(err.response?.data?.error || t('najsport.errorCancel'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleWeekChange = (dir: 'prev' | 'next' | 'today') => {
    if (dir === 'prev') setCurrentWeek(subWeeks(currentWeek, 1));
    else if (dir === 'next') setCurrentWeek(addWeeks(currentWeek, 1));
    else {
      setCurrentWeek(new Date());
      const dow = new Date().getDay();
      setSelectedDayIndex(dow === 0 ? 6 : dow - 1);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('najsportUser');
    localStorage.removeItem('token');
    navigate('/');
  };

  const isToday = (date: Date) => {
    const now = new Date();
    return date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
  };

  const isPastSlot = (slot: TrainingSlot) =>
    isEventDayPast(slot.date, slot.startTime);

  const renderSlotCard = (slot: TrainingSlot) => {
    const booked = myBookings.includes(slot.id);
    const full = slot.isFull && !booked;
    const past = isPastSlot(slot);
    // Booking opens 1 week ahead — whole-day diff on Bratislava calendar days
    // so neither time-of-day nor the viewer's timezone flips the gate
    const slotDay = eventLocalDate(slot.date);
    const today0 = eventLocalDate(new Date().toISOString());
    const daysUntil = Math.round((slotDay.getTime() - today0.getTime()) / 86400000);
    const notYetOpen = !booked && daysUntil > 7;
    const isProcessing = actionLoading === slot.id;

    return (
      <div
        key={slot.id}
        className={`rounded-lg p-3 text-sm transition-shadow hover:shadow-md border ${
          booked ? 'ring-2' : ''
        }`}
        style={{
          backgroundColor: booked ? '#f0ebff' : 'white',
          borderColor: booked ? PURPLE : '#e5e7eb',
          ...(booked ? { boxShadow: `0 0 0 2px ${PURPLE}33` } : {}),
        }}
      >
        <div className="relative flex items-start justify-between gap-1 mb-0.5">
          <div className="font-semibold text-xs leading-snug flex items-center gap-1 flex-wrap" style={{ color: PURPLE }}>
            {slot.title}
            {slot.isSpecial && <span className="inline-block px-1 py-0 rounded text-[9px] font-bold text-white bg-orange-500">SP</span>}
            {slot.isCompetition && <span className="inline-block px-1 py-0 rounded text-[9px] font-bold text-white bg-blue-600">CUP</span>}
            {slot.description && (
              <button
                type="button"
                aria-label={t('najsport.eventInfo')}
                title={t('najsport.eventInfo')}
                onClick={(e) => { e.stopPropagation(); setInfoPinnedId(infoPinnedId === slot.id ? null : slot.id); }}
                onMouseEnter={() => setInfoHoverId(slot.id)}
                onMouseLeave={() => setInfoHoverId(prev => (prev === slot.id ? null : prev))}
                className="text-gray-400 hover:text-purple-600 transition-colors flex items-center"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </button>
            )}
          </div>
          {slot.description && (infoPinnedId === slot.id || infoHoverId === slot.id) && (
            <div
              onClick={(e) => e.stopPropagation()}
              onMouseEnter={() => setInfoHoverId(slot.id)}
              onMouseLeave={() => setInfoHoverId(prev => (prev === slot.id ? null : prev))}
              className="absolute left-0 right-0 top-full mt-1 z-40 bg-white rounded-lg shadow-lg border border-gray-200 p-2.5 text-[11px] font-normal text-gray-700 whitespace-pre-wrap break-words leading-relaxed"
            >
              {slot.description}
            </div>
          )}
          {isTrainerOrAdmin && (
            <div className="relative">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setKebabSlotId(kebabSlotId === slot.id ? null : slot.id); }}
                className="p-2 -mr-1 -mt-1 text-gray-400 hover:text-gray-600 rounded touch-manipulation min-h-[36px] min-w-[36px] flex items-center justify-center"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="2"/><circle cx="12" cy="12" r="2"/><circle cx="12" cy="19" r="2"/></svg>
              </button>
              {kebabSlotId === slot.id && (
                <div className="absolute right-0 top-8 z-50 bg-white rounded-lg shadow-lg border py-1 min-w-[160px]">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setKebabSlotId(null); setEditSlot(slot); }}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-100 active:bg-gray-200 flex items-center gap-2 touch-manipulation"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                    {t('admin.editOccurrence')}
                  </button>
                  {slot.isCompetition && isAdmin && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setKebabSlotId(null); setCompetitionSlot(slot); }}
                      className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-100 active:bg-gray-200 flex items-center gap-2 touch-manipulation"
                    >
                      <span className="text-base leading-none">🏆</span>
                      {t('najsport.setCompetitionResults')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setKebabSlotId(null); navigate(`/admin?edit=${slot.seriesId}`); }}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-100 active:bg-gray-200 flex items-center gap-2 touch-manipulation"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3h18v18H3z"/><path d="M3 9h18M9 3v18"/></svg>
                    {t('admin.editSeries')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
        {slot.trainerName && (
          <div className="text-[10px] text-gray-500 flex items-center gap-1 mb-0.5">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 20h20" />
              <path d="M4 20l2-14 4 6 2-8 2 8 4-6 2 14" />
            </svg>
            <span
              className="hover:underline cursor-pointer"
              onClick={(e) => { e.stopPropagation(); if (slot.trainerId) navigate(`/user/${slot.trainerId}`); }}
            >
              {slot.trainerName}
            </span>
            {slot.trainerRideCount != null && slot.trainerRideCount > 0 && (
              <span className="text-gray-400">({slot.trainerRideCount})</span>
            )}
          </div>
        )}
        <div className="text-[11px] font-medium opacity-80">
          {slot.startTime} - {slot.endTime}
        </div>

        {/* Spots + collapsible attendees */}
        <div className="text-[11px] mt-1 font-medium opacity-70">
          {slot.bookingCount}/{slot.capacity} {t('najsport.spots')}
          {booked && (
            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ backgroundColor: GREEN }}>
              {t('najsport.registered')}
            </span>
          )}
        </div>

        {slot.attendees && slot.attendees.length > 0 && (
          <div className="mt-1">
            <button
              type="button"
              onClick={() => setExpandedAttendees(expandedAttendees === slot.id ? null : slot.id)}
              className="text-[10px] font-semibold text-gray-500 hover:text-gray-700 transition-colors"
            >
              {t('najsport.registeredList')} ({slot.attendees.length}) {expandedAttendees === slot.id ? '▲' : '▼'}
            </button>
            {expandedAttendees === slot.id && (
              <div className="mt-1 space-y-0.5">
                {slot.attendees.map((a) => (
                  <div key={a.id} className="text-[11px] text-gray-600 flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: a.attended ? GREEN : '#D1D5DB' }} />
                    <span
                      className="hover:underline cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); navigate(`/user/${a.id}`); }}
                    >
                      {a.name && a.surname ? `${a.name} ${a.surname}` : a.email}
                    </span>
                    {slot.trainerId === a.id && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={ORANGE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                        <path d="M2 20h20" />
                        <path d="M4 20l2-14 4 6 2-8 2 8 4-6 2 14" />
                      </svg>
                    )}
                  </div>
                ))}
                {isTrainerOrAdmin && (
                  <button
                    type="button"
                    onClick={() => setAttendanceSlot(slot)}
                    className={`mt-1 w-full text-[10px] px-2 py-1 rounded-md font-medium transition-colors ${
                      slot.attendanceConfirmed
                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                        : 'bg-purple-50 hover:bg-purple-100'
                    }`}
                    style={!slot.attendanceConfirmed ? { color: PURPLE } : {}}
                  >
                    {slot.attendanceConfirmed ? t('najsport.attendanceConfirmed') : t('najsport.confirmAttendance')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action icons bar — Strava-style, above CTA */}
        <div className="flex items-center gap-3 mt-2 pt-2 border-t border-gray-100 whitespace-nowrap overflow-x-auto">
          {/* GPX / Map */}
          {isTrainerOrAdmin ? (
            <button
              type="button"
              onClick={() => slot.gpxFileUrl ? setGpxSlot(slot) : setCourseSelectSlot(slot)}
              className="flex items-center gap-1 py-1 rounded-md hover:bg-gray-100 transition-colors group"
              title={slot.courseName ? `${t('najsport.route')}: ${slot.courseName}` : t('najsport.selectRoute')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={slot.gpxFileUrl ? 'opacity-80 group-hover:opacity-100' : 'opacity-30 group-hover:opacity-60'}>
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </button>
          ) : slot.gpxFileUrl ? (
            <button
              type="button"
              onClick={() => setGpxSlot(slot)}
              className="flex items-center gap-1 py-1 rounded-md hover:bg-gray-100 transition-colors group"
              title={slot.courseName ? `${t('najsport.route')}: ${slot.courseName}` : t('najsport.showRoute')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-80 group-hover:opacity-100">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </button>
          ) : null}

          {/* Photos */}
          <button
            type="button"
            onClick={() => setPhotoSlot(slot)}
            className="flex items-center gap-1 py-1 rounded-md hover:bg-gray-100 transition-colors group relative"
            title={t('najsport.photos')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50 group-hover:opacity-100">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            {(slot.photoCount ?? 0) > 0 && (
              <span className="text-[11px] text-gray-500 group-hover:text-gray-700">{slot.photoCount}</span>
            )}
          </button>

          {/* Discussion */}
          <button
            type="button"
            onClick={() => setDiscussionSlot(slot)}
            className="flex items-center gap-1 py-1 rounded-md hover:bg-gray-100 transition-colors group relative"
            title={t('najsport.discussion')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="opacity-50 group-hover:opacity-100">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
            </svg>
            {(slot.commentCount ?? 0) > 0 && (
              <span className="text-[11px] text-gray-500 group-hover:text-gray-700">{slot.commentCount}</span>
            )}
          </button>

          {/* Trainer claim */}
          {isTrainerOrAdmin && (
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation();
                try {
                  const result = await slotsAPI.claimTrainer(slot.id);
                  setSlots(prev => prev.map(s => s.id === slot.id ? { ...s, trainerId: result.trainerId, trainerName: result.trainerName, trainerRideCount: result.trainerRideCount } : s));
                } catch (err) {
                  console.error('Claim trainer error:', err);
                }
              }}
              className="flex items-center gap-1 py-1 rounded-md hover:bg-gray-100 transition-colors group ml-auto"
              title={slot.trainerId === user?.id ? t('najsport.unclaimLeader') : t('najsport.claimLeader')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={slot.trainerId === user?.id ? ORANGE : '#6B7280'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={slot.trainerId === user?.id ? 'opacity-100' : 'opacity-50 group-hover:opacity-100'}>
                <path d="M2 20h20" />
                <path d="M4 20l2-14 4 6 2-8 2 8 4-6 2 14" />
              </svg>
            </button>
          )}
        </div>

        {/* Primary CTA */}
        {!past && (
          booked ? (
            <button
              type="button"
              onClick={() => handleCancel(slot.id)}
              disabled={isProcessing}
              className="mt-2 w-full text-xs px-3 py-1.5 rounded-md font-medium bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 transition-colors"
            >
              {isProcessing ? t('najsport.cancelling') : t('najsport.cancel')}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => handleBook(slot.id)}
              disabled={full || isProcessing || notYetOpen}
              className="mt-2 w-full text-xs px-3 py-1.5 rounded-md font-bold text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              style={{ backgroundColor: notYetOpen ? '#9ca3af' : full ? '#9ca3af' : ORANGE }}
              title={notYetOpen ? t('najsport.opensInDays', { count: daysUntil - 7 }) : undefined}
            >
              {isProcessing
                ? t('najsport.signingUp')
                : notYetOpen
                  ? t('najsport.notYetOpen')
                  : full
                    ? t('najsport.full')
                    : t('najsport.signUp')}
            </button>
          )
        )}
      </div>
    );
  };

  if (!user) return null;

  return (
    <div ref={topRef} className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="shadow-md" style={{ backgroundColor: PURPLE }}>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/najsport-logo.png" alt="NajSport" className="h-8 w-auto" />
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/leaderboard')}
              className="text-sm text-orange-300 hover:text-orange-100 font-medium transition-colors"
            >
              🏆
            </button>
            {(user.role === 'TRAINER' || user.role === 'ADMIN' || user.role === 'POWER_ADMIN') && (
              <button
                onClick={() => navigate('/admin')}
                className="text-sm text-orange-300 hover:text-orange-100 font-medium transition-colors"
              >
                {t('najsport.admin')}
              </button>
            )}
            <button
              onClick={() => navigate('/profile')}
              className="transition-opacity hover:opacity-80"
            >
              {user.photoUrl ? (
                <img
                  src={resolveFileUrl(user.photoUrl)}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover border-2 border-purple-300"
                />
              ) : (
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-purple-300"
                  style={{ backgroundColor: PURPLE }}
                >
                  {((user.name?.[0] || '') + (user.surname?.[0] || '')).toUpperCase() || user.email?.[0]?.toUpperCase() || '?'}
                </div>
              )}
            </button>
            <button
              onClick={handleLogout}
              className="text-sm text-purple-300 hover:text-white underline transition-colors"
            >
              {t('najsport.logout')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 sm:py-6">
        {/* Week Navigation */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl sm:text-2xl font-bold hidden md:block" style={{ color: PURPLE }}>
            {t('najsport.rides')}
          </h2>
          <div className="flex items-center gap-2 w-full md:w-auto justify-between md:justify-end">
            <button
              onClick={() => handleWeekChange('prev')}
              className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm font-medium transition-colors"
            >
              {t('najsport.prev')}
            </button>
            <button
              onClick={() => handleWeekChange('today')}
              className="px-3 py-2 rounded-lg text-white text-sm font-medium transition-colors"
              style={{ backgroundColor: PURPLE }}
            >
              {t('najsport.today')}
            </button>
            <span className="text-sm font-medium text-gray-700 min-w-[160px] text-center">
              {format(weekStart, 'd MMM')} – {format(weekEnd, 'd MMM yyyy')}
              {loading && <span className="ml-2 inline-block w-3 h-3 border-2 border-gray-300 rounded-full animate-spin" style={{ borderTopColor: PURPLE }} />}
            </span>
            <button
              onClick={() => handleWeekChange('next')}
              className="px-3 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 text-sm font-medium transition-colors"
            >
              {t('najsport.next')}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        {/* Mobile: Day selector */}
        <div className="md:hidden mb-4 flex gap-1">
          {dayDates.map((date, idx) => {
            const today = isToday(date);
            const selected = idx === selectedDayIndex;
            const daySlots = slotsByDay[DAY_KEYS[idx]] || [];
            const hasSlots = daySlots.length > 0;
            return (
              <button
                key={idx}
                onClick={() => setSelectedDayIndex(idx)}
                className={`flex-1 py-3 rounded-lg text-center transition-colors ${
                  selected
                    ? 'text-white font-bold'
                    : today
                      ? 'bg-purple-50 font-medium'
                      : 'bg-white'
                } ${!hasSlots && !selected ? 'opacity-50' : ''}`}
                style={selected ? { backgroundColor: PURPLE } : undefined}
              >
                <div className="text-xs uppercase">{t(DAY_SHORT_I18N[idx])}</div>
                <div className="text-base font-semibold">{format(date, 'd')}</div>
                {hasSlots && !selected && (
                  <div className="w-1.5 h-1.5 rounded-full mx-auto mt-0.5" style={{ backgroundColor: ORANGE }} />
                )}
              </button>
            );
          })}
        </div>

        {/* Mobile: Single day view */}
        <div className="md:hidden">
          {loading ? (
            <div className="space-y-3">
              {[1, 2].map((i) => (
                <div key={i} className="rounded-lg p-3 bg-white border border-gray-200 animate-pulse">
                  <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
                  <div className="h-2.5 bg-gray-200 rounded w-1/3 mb-3" />
                  <div className="h-2.5 bg-gray-200 rounded w-1/2 mb-2" />
                  <div className="h-8 bg-gray-200 rounded w-full mt-3" />
                </div>
              ))}
            </div>
          ) : (() => {
            const daySlots = slotsByDay[DAY_KEYS[selectedDayIndex]] || [];
            return daySlots.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                {t('najsport.noRides')} — {format(dayDates[selectedDayIndex], 'EEEE, d. MMM')}
              </div>
            ) : (
              <div className="space-y-3">
                {daySlots.map(renderSlotCard)}
              </div>
            );
          })()}
        </div>

        {/* Desktop: 7-column grid */}
        <div className="hidden md:grid grid-cols-7 gap-3">
          {dayDates.map((date, idx) => {
            const today = isToday(date);
            const daySlots = slotsByDay[DAY_KEYS[idx]] || [];
            return (
              <div
                key={idx}
                className={`rounded-xl p-2 min-h-[200px] ${
                  today ? 'border-2 bg-purple-50/50' : 'bg-white border'
                }`}
                style={today ? { borderColor: PURPLE } : undefined}
              >
                <div className={`text-center py-2 mb-2 rounded-lg ${today ? 'text-white' : 'text-gray-700'}`}
                  style={today ? { backgroundColor: PURPLE } : undefined}
                >
                  <div className="text-[10px] uppercase font-medium">{t(DAY_SHORT_I18N[idx])}</div>
                  <div className="text-lg font-bold">{format(date, 'd')}</div>
                </div>
                <div className="space-y-2">
                  {daySlots.map(renderSlotCard)}
                  {daySlots.length === 0 && (
                    <div className="text-center text-gray-300 text-xs py-4">—</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 text-center">
          <a
            href="https://www.najsport.sk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block text-sm font-medium hover:underline transition-colors"
            style={{ color: ORANGE }}
          >
            {t('najsport.needSomething')}
          </a>
        </div>
      </main>


      {/* Discussion Modal */}
      {discussionSlot && (
        <DiscussionModal
          slot={discussionSlot}
          currentUser={user}
          onClose={() => setDiscussionSlot(null)}
        />
      )}

      {/* Edit single occurrence */}
      {editSlot && (
        <EditChildModal
          child={{
            id: editSlot.id,
            title: editSlot.title,
            description: editSlot.description ?? null,
            date: editSlot.date,
            startTime: editSlot.startTime,
            endTime: editSlot.endTime ?? null,
            capacity: editSlot.capacity,
            cancelled: editSlot.cancelled,
            courseId: editSlot.courseId ?? null,
            trainerId: editSlot.trainerId ?? null,
            isSpecial: editSlot.isSpecial,
            isCompetition: editSlot.isCompetition,
          }}
          onClose={() => setEditSlot(null)}
          onSaved={() => fetchWeek()}
        />
      )}

      {/* Competition Results Modal */}
      {competitionSlot && (
        <CompetitionResultsModal
          slot={competitionSlot}
          onClose={() => setCompetitionSlot(null)}
          onSaved={() => fetchWeek()}
        />
      )}

      {/* Equipment Reminder Modal */}
      {bookingSlotId && (
        <EquipmentReminderModal
          onConfirm={() => handleBookConfirmed(bookingSlotId)}
          onCancel={() => setBookingSlotId(null)}
        />
      )}

      {/* Attendance Modal (admin) */}
      {attendanceSlot && (
        <AttendanceModal
          slot={attendanceSlot}
          onClose={() => setAttendanceSlot(null)}
          onConfirmed={() => { setAttendanceSlot(null); fetchWeek(); }}
        />
      )}

      {/* Course Selector Modal (admin) */}
      {courseSelectSlot && (
        <CourseSelectorModal
          slot={courseSelectSlot}
          onClose={() => setCourseSelectSlot(null)}
          onChanged={() => fetchWeek()}
          onViewMap={(s) => setGpxSlot(s)}
        />
      )}

      {/* GPX Map Modal */}
      {gpxSlot && (
        <GpxMapModal
          slot={gpxSlot}
          onClose={() => setGpxSlot(null)}
          onChangeCourse={isTrainerOrAdmin ? () => { setGpxSlot(null); setCourseSelectSlot(gpxSlot); } : undefined}
        />
      )}

      {/* Photo Gallery Modal */}
      {photoSlot && (
        <PhotoGalleryModal
          slot={photoSlot}
          userId={user?.id || null}
          isAdmin={isAdmin}
          onClose={() => setPhotoSlot(null)}
          onPhotosChanged={() => fetchWeek()}
        />
      )}

      {/* Cancel Confirmation Modal */}
      {cancelConfirmSlotId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setCancelConfirmSlotId(null)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white w-[85%] max-w-xs rounded-2xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4" style={{ backgroundColor: PURPLE }}>
              <h3 className="text-sm font-bold text-white">{t('najsport.cancelConfirmTitle')}</h3>
            </div>
            <div className="px-5 py-4">
              <p className="text-sm text-gray-600 mb-4">{t('najsport.cancelConfirmText')}</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setCancelConfirmSlotId(null)}
                  className="flex-1 py-2 px-3 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  {t('najsport.cancelNo')}
                </button>
                <button
                  type="button"
                  onClick={handleCancelConfirmed}
                  className="flex-1 py-2 px-3 rounded-lg text-sm font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors"
                >
                  {t('najsport.cancelYes')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NajSportEvents;
